import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Box,
  Button,
} from '@mui/material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';

interface Form {
  id: string;
  name: string;
}

interface AddFormsToCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  categoryId: string;
  existingForms: Form[];
}

export const AddFormsToCategoryDialog: React.FC<AddFormsToCategoryDialogProps> = ({
  open,
  onClose,
  categoryId,
  existingForms,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [forms, setForms] = useState<Form[]>([]);
  const [selectedForms, setSelectedForms] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      dataProvider
        .getList('forms', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        })
        .then(({ data }) => {
          setForms(data);
          setSelectedForms(existingForms.map((form) => form.id));
        })
        .catch(() => {
          notify('Error: Could not fetch forms', { type: 'warning' });
        });
    }
  }, [open, dataProvider, notify, existingForms]);

  const handleToggle = (formId: string) => {
    const currentIndex = selectedForms.indexOf(formId);
    const newSelectedForms = [...selectedForms];

    if (currentIndex === -1) {
      newSelectedForms.push(formId);
    } else {
      newSelectedForms.splice(currentIndex, 1);
    }

    setSelectedForms(newSelectedForms);
  };

  const handleSave = async () => {
    try {
      await dataProvider.update('task-categories', {
        id: categoryId,
        data: {
          forms: selectedForms,
        },
        previousData: { id: categoryId },
      });
      notify('Forms updated');
      refresh();
      onClose();
    } catch (error) {
      notify('Error: Could not update forms', { type: 'warning' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Forms to Category</DialogTitle>
      <DialogContent>
        <List>
          {forms.map((form) => (
            <ListItem
              key={form.id}
              dense
              button
              onClick={() => handleToggle(form.id)}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={selectedForms.indexOf(form.id) !== -1}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={form.name} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};
